import { multiPolygon, polygon } from '@turf/helpers';
import centerOfMass from '@turf/center-of-mass';
import _bbox from '@turf/bbox';
import distance from '@turf/distance';
// eslint-disable-next-line import/no-unresolved
import { P } from '@ui-kit/ui-kit';
import { pick, get, set } from 'lodash-es';

export type leanListingCategory = 'Over valued' | 'Fair price' | 'Good price' | 'Great price' | 'Rare find' | 'Hidden gem';

export type LeanListing = Partial<P.Listing> & {
  category: leanListingCategory;
  categoryAlias: string;
  icon: string;
  favourite?: boolean;
};

export type LeanNeighbourhood = Partial<P.Msoa> & {
  geoJson: any;
  style: LeanMsoaStyle;
  bbox?: string;
  bounds?: any;
  center?: number[];
  icon?: string;
  paths?: any;
  selected?: boolean;
};

export interface LeanMsoaStyle {
  fillColor?: string;
  fillOpacity?: number;
  strokeColor?: string;
  strokeWeight?: number;
  strokeOpacity?: number;
  clickable?: boolean;
  zIndex?: number;
}

export const PLACEHOLDER_LEAN_LISTING: LeanListing = {
  id: null,
  category: null,
  categoryAlias: null,
  icon: null,
  favourite: false,
  createdAt: null,
  metadata: {
    coordinates: [null, null],
    price: null,
    price_offset: null,
    num_bedrooms: null,
    property_type: null,
    surface_area: { sqm: null },
    msoa_code: null,
    price_change_since_listed: null,
    price_change_since_listed_pct: null,
  } as P.ListingMetadata,
  source_data: {
    listing_id: null,
    displayable_address: null,
    details_url: null,
    address: null,
    image_url: null,
  } as P.ListingSourceData,
};

export const defaultImage = '/assets/illustrations/splash-home-default.svg';

/**
 * return price category
 */
export function categorize(price: number, ppsqmOffset: number, priceChangeSinceListedPercent: number = 0): {
  category: leanListingCategory;
  marketValue: number;
  marketValueRange: number[];
} {
  if (!price || typeof ppsqmOffset !== 'number') {
    return {
      category: null,
      marketValue: null,
      marketValueRange: [null, null],
    };
  }

  let category: leanListingCategory = 'Fair price';

  if (ppsqmOffset > 0.25) {
    ppsqmOffset = 0.25;
  }

  if (ppsqmOffset < -0.25) {
    ppsqmOffset = -0.25;
  }

  const marketValue = price * (1 - ppsqmOffset);
  const marketValueRange = [marketValue * 0.9, marketValue];

  // -0%+ under valued
  const overvalued: boolean = ppsqmOffset > 0;

  if (overvalued) {
    category = 'Over valued';
  }

  // 10%+ under valued
  const greatPrice: boolean = ppsqmOffset < -0.1;

  if (greatPrice) {
    // return 'Good price';
    category = 'Great price';
  }

  // 10%+ under valued and 10%+ discounted
  const rareFind: boolean = ppsqmOffset < -0.1
    && priceChangeSinceListedPercent <= -0.1;

  if (rareFind) {
    // return 'Rare find';
    category = 'Hidden gem';
  }

  return {
    category,
    marketValue,
    marketValueRange,
  };
}

/**
 * map listing
 */
export function mapToLeanListing(
  listing: P.Listing,
  index?: number,
  listings?: P.Listing[],
  favourited?: (string | P.Listing)[],
  enquiry?: P.Enquiry,
): LeanListing {
  // keys used from original payload
  const keys = [
    'id',
    'createdAt',
    'eligibleUpTo',
    'metadata.coordinates',
    'metadata.price',
    'metadata.price_offset',
    'metadata.num_bedrooms',
    'metadata.property_type',
    'metadata.surface_area',
    'metadata.msoa_code',
    'metadata.price_change_since_listed',
    'metadata.price_change_since_listed_pct',
    'source_data.listing_id',
    'source_data.displayable_address',
    'source_data.details_url',
    'source_data.address', // external enquiries
    'source_data.image_url',
  ];

  if (enquiry) {
    set(listing, 'metadata.price', enquiry?.price || listing?.metadata?.price);
    set(listing, 'metadata.property_type', enquiry?.property_type || listing?.metadata?.property_type);
    set(listing, 'metadata.surface_area', enquiry?.surface_area || listing?.metadata?.surface_area);
    set(listing, 'metadata.price_offset', (enquiry as any)?.lending_decision_metadata?.price_offset || listing?.metadata?.price_offset);
    set(
      listing,
      'metadata.ppsqm_forecast',
      (enquiry as any)?.lending_decision_metadata?.ppsqm_forecast || listing?.metadata?.ppsqm_forecast,
    );
  }

  // ensure price_offset is not under or over 25%
  // tslint:disable-next-line: variable-name
  let ppsqmOffset = get(listing, 'metadata.price_offset');
  if (ppsqmOffset > 0.25) {
    ppsqmOffset = 0.25;
  }
  if (ppsqmOffset < -0.25) {
    ppsqmOffset = -0.25;
  }

  // tslint:disable-next-line: variable-name
  const imageUrl = get(listing, 'source_data.image_url') || defaultImage;
  // tslint:disable-next-line: variable-name
  const image645x430Url = get(listing, 'source_data.image_645_430_url') || defaultImage;
  // tslint:disable-next-line: variable-name
  const sourceData = { ...get(listing, 'source_data'), imageUrl, image645x430Url };

  // make sure coordinates array exists
  const coordinates = get(listing, 'metadata.coordinates') || [];

  // update price_offset
  const metadata = { ...get(listing, 'metadata'), ppsqmOffset, coordinates };

  // categorize price
  const category = categorize(listing.metadata?.price, get(metadata, 'price_offset'), get(metadata, 'price_change_since_listed_pct'));

  // category alias
  const categoryAlias = category.category
    ? category.category
      .toLowerCase()
      .replace(' ', '-')
    : null;

  // marker icon for category
  const icon = `/assets/marker/marker-${categoryAlias}.png`;

  // check if is favourited listings
  const favourite: boolean = (favourited || [])
    .map((id) => id)
    .indexOf(listing?.id) > -1 || listing.id === ((enquiry?.listing as any)?.id || enquiry?.listing);

  return {
    ...pick(listing, keys),
    category: category.category,
    categoryAlias,
    icon,
    favourite,
    metadata,
    source_data: sourceData,
  };
}

/**
 * map neighbourhood (msoa)
 */
export function mapToLeanNeighbourhood(msoa: P.Msoa, overrideStyle: LeanMsoaStyle = {}): LeanNeighbourhood {
  // keys used from original payload
  const keys = [
    'code',
    'zone',
    'msoa11hclnm',
    'photo',
  ];

  // base google maps layer styles
  const style = {
    strokeColor: '#000',
    strokeWeight: 1,
    strokeOpacity: 0.1,
    fillOpacity: 0,
    ...overrideStyle,
  };

  // sanitize geoJson
  const geoJson = {
    type: 'Feature',
    geometry: sanitizeGeometry(msoa.geometry),
    properties: {
      msoa: msoa.code,
      style: style as LeanMsoaStyle,
    },
  };

  // center of neighbourhood
  const center = centerOfGeometry(geoJson.geometry);

  // bounding box of neighbourhood
  const bBox = _bbox(geoJson.geometry);
  const bbox: string = [bBox[1], bBox[0], bBox[3], bBox[2]]
    .join(',');

  // google maps lat lng bounds
  const bounds = bboxToLatLngBoundsLiteral(bbox);

  // neighbourhood marker
  const icon = '/assets/marker/marker-star.png';

  const paths = geoJson.geometry.coordinates[0].map((coordinates) => ({ lat: coordinates[1], lng: coordinates[0] }));

  return {
    ...pick(msoa, keys),
    geoJson,
    style,
    bbox,
    bounds,
    center,
    icon,
    paths,
    selected: false,
  };
}

/**
 * convert bbox to google maps lat lng bounds
 */
export function bboxToLatLngBoundsLiteral(bbox: string): any {
  const bBox = bbox
    .split(',')
    .map(parseFloat);

  const bounds: any = {
    south: get(bBox, '0') || 0,
    west: get(bBox, '1') || 0,
    north: get(bBox, '2') || 0,
    east: get(bBox, '3') || 0,
  };

  return bounds;
}

/**
 * get center of bbox as google maps lat lng literal
 */
export function bboxCenterToLatLngLiteral(bboxString: string): any {
  const bbox = bboxString
    .split(',')
    .map(parseFloat);

  const bboxGeometry = polygon(
    [[[bbox[3], bbox[0]],
      [bbox[1], bbox[0]],
      [bbox[1], bbox[2]],
      [bbox[3], bbox[2]],
      [bbox[3], bbox[0]]]],
  );

  const center = centerOfGeometry(bboxGeometry);

  return { lng: center[0], lat: center[1] };
}

/**
 * convert google maps lat lng bounds to bbox
 */
export function latLngBoundsLiteralToBbox(bounds: any): string {
  const bbox = [
    bounds.south,
    bounds.west,
    bounds.north,
    bounds.east,
  ];

  return bbox.join(',');
}

/**
 * sanitize geometry
 */
export function sanitizeGeometry(geometry: any): any {
  switch (geometry?.type) {
    case 'GeometryCollection':
      return multiPolygon(geometry.geometries
        .map((innerGeometry) => innerGeometry.coordinates)).geometry;
    default:
      return geometry;
  }
}

/**
 * get distance (km) between center ot two geometries
 */
export function distanceBetweenCenterOfGeometries(a, b): number {
  try {
    if (a.type === 'MultiPolygon') {
      a = polygon(a.coordinates[0]);
    }

    const centerA = centerOfMass(a);

    if (b.type === 'MultiPolygon') {
      b = polygon(b.coordinates[0]);
    }

    const centerB = centerOfMass(b);

    return distance(centerA, centerB);
  } catch (error) {
    console.error(error);
    return -1;
  }
}

/**
 * get center of geometry
 */
export function centerOfGeometry(geometry): number[] {
  if (geometry.type === 'MultiPolygon') {
    geometry = polygon(geometry.coordinates[0]);
  }

  const center = centerOfMass(geometry);

  return center.geometry.coordinates;
}

export const defaultErrorMessage = 'Oops, an error has occurred. Please try again. If the problem persists - '
+ 'please contact support@proportunity.com.';
